/// Renders terrain obstacles as semi-transparent alert-tinted polygons
/// with their elevation labelled at the centroid. Used by mountainous
/// levels (Innsbruck, Aspen, etc.) so the player can see the
/// no-fly-below floor at a glance.

import { Container, Graphics, Text } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { TerrainObstacle } from '../types/LevelData'
import { radarTheme } from './theme'

export class TerrainLayer extends Container {
  constructor(obstacles: TerrainObstacle[], projector: CoordinateProjector) {
    super()
    for (const obstacle of obstacles) {
      this.addChild(buildObstacle(obstacle, projector))
    }
  }
}

function buildObstacle(o: TerrainObstacle, projector: CoordinateProjector): Container {
  const node = new Container()
  const g = new Graphics()
  const points: number[] = []
  for (const c of o.coordinates) {
    const p = projector.project(c, true)
    points.push(p.x, p.y)
  }
  if (points.length >= 6) {
    g.poly(points)
    g.fill({ color: radarTheme.alert, alpha: 0.18 })
    g.stroke({ width: 1, color: radarTheme.alert, alpha: 0.6 })
  }
  node.addChild(g)

  // Elevation label at the centroid.
  const cx =
    points.filter((_, i) => i % 2 === 0).reduce((s, v) => s + v, 0) /
    (points.length / 2)
  const cy =
    points.filter((_, i) => i % 2 === 1).reduce((s, v) => s + v, 0) /
    (points.length / 2)
  const label = new Text({
    text: `${o.elevationInFeet.toLocaleString()} ft`,
    style: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 10,
      fill: radarTheme.alert,
    },
  })
  label.alpha = 0.9
  label.anchor.set(0.5)
  label.position.set(cx, cy)
  node.addChild(label)

  return node
}
